/**
 * Redux selectors for the chat slice.
 * Provides memoized selectors for accessing chat state.
 */

import { createSelector } from '@reduxjs/toolkit'
import type { ChatMessageEntity, PaginationState } from './chat-message-entity.interface'
import type { ChatState } from './chat-state.interface'

const selectChatState = (state: { chat: ChatState }): ChatState => state.chat

export const selectActiveConversationId = createSelector(
  [selectChatState],
  (chat) => chat.activeConversationId
)

export const selectActiveConversation = createSelector(
  [selectChatState],
  (chat) => chat.activeConversation
)

export const selectMessageEntities = createSelector(
  [selectChatState],
  (chat) => chat.messageEntities
)

export const selectMessageIdsByConversation = createSelector(
  [selectChatState],
  (chat) => chat.messageIdsByConversation
)

export const selectPaginationByConversation = createSelector(
  [selectChatState],
  (chat) => chat.paginationByConversation
)

export const selectMessagesForConversation = createSelector(
  [
    selectMessageEntities,
    selectMessageIdsByConversation,
    (_: unknown, conversationId: string) => conversationId,
  ],
  (entities, idsByConversation, conversationId): ChatMessageEntity[] => {
    const idList = idsByConversation[conversationId] ?? []
    return idList.map((id) => entities[id]).filter(Boolean)
  }
)

export const selectPaginationForConversation = createSelector(
  [selectPaginationByConversation, (_: unknown, conversationId: string) => conversationId],
  (paginationMap, conversationId): PaginationState =>
    paginationMap[conversationId] ?? { page: 1, hasMore: false }
)

export const selectHasMoreMessages = createSelector(
  [selectPaginationForConversation],
  (pagination): boolean => pagination.hasMore
)

export const selectPageForConversation = createSelector(
  [selectPaginationForConversation],
  (pagination): number => pagination.page
)

export const selectPendingMessage = createSelector([selectChatState], (chat) => chat.pendingMessage)

export const selectIsAnyMessageStreaming = createSelector(
  [selectMessageEntities],
  (entities): boolean => Object.values(entities).some((entity) => entity.isStreaming)
)

export const selectHasAnyPendingMessage = createSelector(
  [selectMessageEntities],
  (entities): boolean => Object.values(entities).some((entity) => entity.isPending)
)

export const selectIsSendingMessage = createSelector(
  [selectIsAnyMessageStreaming, selectHasAnyPendingMessage],
  (isStreaming, hasPending): boolean => isStreaming || hasPending
)

export const selectIsConversationLocallyCreated = createSelector(
  [selectChatState, (_: unknown, conversationId: string) => conversationId],
  (chat, conversationId): boolean => chat.locallyCreatedConversationIds[conversationId] === true
)

export const selectIsConversationStopping = createSelector(
  [selectChatState, (_: unknown, conversationId: string) => conversationId],
  (chat, conversationId): boolean => chat.isStoppingByConversationId[conversationId] === true
)

export const selectIsOrchestrationActive = createSelector(
  [selectChatState, (_: unknown, conversationId: string) => conversationId],
  (chat, conversationId): boolean =>
    chat.isOrchestrationActiveByConversationId[conversationId] === true
)
